import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '../../../../@shared/services/user.service';
import {
  VipDashboardCase,
  VipDashboardCascadeSlot,
  VipDashboardLevelReward,
  VipDashboardQuest,
  VipDashboardResponse,
  VipQuestLogItem,
  VipQuestLogMeta,
  VipQuestLogsQuery,
  VipQuestLogsResponse,
} from '../../../../@shared/services/vip.models';
import { VipFacade } from '../../../../@shared/services/vip.facade';

type VipQuestStripStatus = 'claimed' | 'completed-awaiting-claim' | 'in-progress' | 'locked';

interface VipCaseRow {
  period: string;
  currentStage: number;
  valueLabel: string;
  claimStatus: string;
}

interface VipSummaryCard {
  title: string;
  value: string;
  hint: string;
}

interface VipBonusDetail {
  title: string;
  description: string;
}

interface VipQuestStripView {
  questNumber: number;
  progressPercent: number;
  progressLabel: string;
  status: VipQuestStripStatus;
  statusLabel: string;
}

interface VipQuestGameView {
  gameLabel: string;
  gameSlug: string;
  questDate?: string;
  currentQuest: number;
  currentQuestCursor: number;
  currentQuestLabel: string;
  currentProgress: number;
  claimedUpTo: number;
  claimedCount: number;
  readyToClaimCount: number;
  completedCount: number;
  quests: VipQuestStripView[];
}

@Component({
  selector: 'app-vip',
  templateUrl: './vip.component.html',
  styleUrls: ['./vip.component.scss'],
})
export class VipComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private currentUserId: number | null = null;
  private dashboardRequestVersion = 0;
  private questLogRequestVersion = 0;

  public isDashboardLoading = true;
  public dashboardError: string | null = null;
  public dashboard: VipDashboardResponse | null = null;

  public isQuestLogLoading = false;
  public questLogError: string | null = null;
  public questLogItems: VipQuestLogItem[] = [];
  public questLogMeta: VipQuestLogMeta = {};
  public readonly questLogColumns: string[] = ['time', 'wagerAmount', 'xpContributed', 'progress', 'status'];
  public readonly questPageSizeOptions: number[] = [10];
  public questLogPageSize = 10;
  public questLogPageIndex = 0;

  public readonly cascadeColumns: string[] = ['slotType', 'bonusCount', 'isClaimed'];
  public readonly caseColumns: string[] = ['period', 'currentStage', 'valueLabel', 'claimStatus'];
  public readonly rewardColumns: string[] = ['level', 'reward', 'claimed'];

  public questGames: VipQuestGameView[] = [];
  public selectedGameSlug: string | null = null;
  public selectedQuestNumber: number | null = null;

  public caseData: VipCaseRow[] = [];

  constructor(
    private readonly vipFacade: VipFacade,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.userService.currentUserId
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId: number) => {
        if (!userId) {
          return;
        }

        this.currentUserId = userId;
        this.resetViewState();
        this.loadDashboard(userId);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get tierName(): string {
    return this.dashboard?.xp?.tierSlug || this.dashboard?.profile?.tier?.slug || '—';
  }

  get level(): number {
    return this.dashboard?.xp?.currentLevel || 0;
  }

  get totalXp(): string {
    const xp = this.dashboard?.xp?.totalXp;
    return xp != null ? Number(xp).toLocaleString() : '0';
  }

  get xpToNextLevel(): string {
    const xp = this.dashboard?.xp?.xpToNextLevel;
    return xp != null ? Number(xp).toLocaleString() : '—';
  }

  get xpToNextLevelLabel(): string {
    const xp = this.dashboard?.xp?.xpToNextLevel;
    return xp != null ? `${Number(xp).toLocaleString()} XP осталось` : 'Нет данных по следующему уровню';
  }

  get kCoefficient(): string {
    const k = this.dashboard?.profile?.kCoefficient;
    return k != null ? (k / 1000).toFixed(3) : '—';
  }

  get streakDays(): number {
    return this.dashboard?.streak?.currentStreak || 0;
  }

  get streakBonus(): string {
    const pct = this.dashboard?.streak?.bonusPercent;
    return pct != null ? `${pct}%` : '0%';
  }

  get multiplier(): string {
    const m = this.dashboard?.multiplier;
    return m != null ? `×${(m / 100).toFixed(1)}` : '×1.0';
  }

  get questsCompleted(): number {
    return this.questGames.reduce((sum: number, game: VipQuestGameView) => sum + game.completedCount, 0);
  }

  get questsTotal(): number {
    return this.questGames.length * 10;
  }

  get cascadeSlots(): VipDashboardCascadeSlot[] {
    return this.dashboard?.cascade || [];
  }

  get cases(): VipDashboardCase[] {
    return this.dashboard?.cases || [];
  }

  get levelRewards(): VipDashboardLevelReward[] {
    return this.dashboard?.levelRewards || [];
  }

  get unclaimedRewards(): number {
    return this.levelRewards.filter((reward: VipDashboardLevelReward) => !reward.claimed).length;
  }

  get claimableRewards(): number {
    return this.levelRewards.filter(
      (reward: VipDashboardLevelReward) => !reward.claimed && reward.level <= this.level,
    ).length;
  }

  get claimedCases(): number {
    return this.cases.filter((vipCase: VipDashboardCase) => vipCase.isClaimed).length;
  }

  get availableCascadeBonuses(): number {
    return this.cascadeSlots.filter(
      (slot: VipDashboardCascadeSlot) => !slot.isClaimed && slot.bonusCount >= slot.maxBonuses,
    ).length;
  }

  get summaryCards(): VipSummaryCard[] {
    return [
      {
        title: 'Статус',
        value: `${this.tierName.toUpperCase()} / ${this.level} ур.`,
        hint: `K-коэфф. ${this.kCoefficient}`,
      },
      {
        title: 'XP',
        value: this.totalXp,
        hint: this.xpToNextLevelLabel,
      },
      {
        title: 'Квесты',
        value: `${this.questsCompleted} / ${this.questsTotal || 0}`,
        hint: this.selectedGame
          ? `${this.selectedGame.gameLabel}: ${this.selectedGame.currentQuestLabel}`
          : 'Квестовые игры не найдены',
      },
      {
        title: 'Награды',
        value: `${this.claimableRewards} готово / ${this.unclaimedRewards} не получено`,
        hint: `${this.claimedCases} из ${this.cases.length} кейсов уже открыто`,
      },
    ];
  }

  get bonusDetails(): VipBonusDetail[] {
    return [
      {
        title: 'XP множитель',
        description: `Текущий множитель: ${this.multiplier}. Это фактическое значение из dashboard.multiplier.`,
      },
      {
        title: 'Стрик',
        description: `Серия входов: ${this.streakDays} дн. с бонусом ${this.streakBonus}.`,
      },
      {
        title: 'Каскад',
        description: this.cascadeSlots.length > 0
          ? `${this.availableCascadeBonuses} из ${this.cascadeSlots.length} слотов уже заполнены и ждут получения.`
          : 'В ответе API нет каскадных слотов для этого пользователя.',
      },
      {
        title: 'Награды по уровням',
        description: this.levelRewards.length > 0
          ? `${this.claimableRewards} наград доступны уже сейчас, ещё ${this.unclaimedRewards - this.claimableRewards} откроются на следующих уровнях.`
          : 'В ответе API нет наград по уровням для этого пользователя.',
      },
    ];
  }

  get apiLimitNotes(): string[] {
    return [
      'Точный процент прогресса внутри текущего уровня не отображается: API возвращает totalXp и xpToNextLevel, но не отдаёт XP внутри уровня.',
      'Логи квестов загружаются отдельно и постранично, поэтому смена квеста не блокирует загрузку общей VIP сводки.',
    ];
  }

  get isExcluded(): boolean {
    return this.dashboard?.profile?.isExcluded === true;
  }

  get exclusionReason(): string {
    return this.dashboard?.profile?.exclusionReason || '';
  }

  get selectedGame(): VipQuestGameView | null {
    if (!this.selectedGameSlug) {
      return null;
    }

    return this.questGames.find((game: VipQuestGameView) => game.gameSlug === this.selectedGameSlug) || null;
  }

  get selectedQuest(): VipQuestStripView | null {
    if (this.selectedQuestNumber == null) {
      return null;
    }

    return this.selectedGame?.quests.find((quest: VipQuestStripView) => quest.questNumber === this.selectedQuestNumber) || null;
  }

  get questLogTotalRows(): number {
    const count = this.questLogMeta.count;

    if (typeof count === 'number') {
      return count;
    }

    if (typeof count === 'string' && count.length > 0) {
      return Number(count);
    }

    return this.questLogItems.length;
  }

  public selectGame(gameSlug: string | null): void {
    if (!gameSlug || gameSlug === this.selectedGameSlug) {
      return;
    }

    this.selectedGameSlug = gameSlug;
    this.selectedQuestNumber = this.getInitialQuestNumber(this.selectedGame);
    this.questLogPageIndex = 0;
    this.loadSelectedQuestLogs();
  }

  public selectQuest(quest: VipQuestStripView): void {
    if (this.selectedQuestNumber === quest.questNumber) {
      return;
    }

    this.selectedQuestNumber = quest.questNumber;
    this.questLogPageIndex = 0;
    this.loadSelectedQuestLogs();
  }

  public isQuestSelected(quest: VipQuestStripView): boolean {
    return this.selectedQuestNumber === quest.questNumber;
  }

  public pageChanged(event: PageEvent): void {
    if (
      event.pageIndex === this.questLogPageIndex &&
      event.pageSize === this.questLogPageSize
    ) {
      return;
    }

    this.questLogPageIndex = event.pageIndex;
    this.questLogPageSize = event.pageSize;
    this.loadSelectedQuestLogs();
  }

  public formatReward(reward: VipDashboardLevelReward): string {
    if (
      reward.rewardType === 'xp_boost' &&
      reward.rewardData?.multiplier != null &&
      reward.rewardData.durationMinutes != null
    ) {
      return `x${reward.rewardData.multiplier / 100} XP на ${reward.rewardData.durationMinutes} мин`;
    }

    if (reward.rewardType === 'coins' && reward.rewardData?.amount != null) {
      return `$${(reward.rewardData.amount / 100).toFixed(2)}`;
    }

    if (reward.rewardCents != null) {
      return `$${(reward.rewardCents / 100).toFixed(2)}`;
    }

    return reward.rewardType || '—';
  }

  public formatRewardStatus(reward: VipDashboardLevelReward): string {
    if (reward.claimed) {
      return 'Получено';
    }

    if (reward.level <= this.level) {
      return 'Доступно к получению';
    }

    return `Откроется на ${reward.level} ур.`;
  }

  public formatLogTime(item: VipQuestLogItem): string | null {
    return this.getStringField(item, ['timestamp', 'createdAt', 'occurredAt', 'time']);
  }

  public formatLogWager(item: VipQuestLogItem): string {
    const cents = this.getNumberField(item, [
      'wagerAmountCents',
      'wagerCents',
      'betAmountCents',
      'amountCents',
    ]);

    if (cents != null) {
      return `$${(cents / 100).toFixed(2)}`;
    }

    const amount = this.getNumberField(item, [
      'wagerAmount',
      'wager',
      'betAmount',
      'amount',
    ]);

    return amount != null ? this.formatCompactNumber(amount) : '—';
  }

  public formatLogXp(item: VipQuestLogItem): string {
    const xpAmount = this.getNumberField(item, [
      'xpContributed',
      'xpAmount',
      'xp',
      'contributedXp',
      'awardedXp',
    ]);

    if (xpAmount == null) {
      return '—';
    }

    return `+${this.formatCompactNumber(xpAmount)}`;
  }

  public formatLogProgress(item: VipQuestLogItem): string {
    const before = this.getNumberField(item, ['progressBefore', 'beforeProgress', 'before']);
    const after = this.getNumberField(item, ['progressAfter', 'afterProgress', 'after']);
    const delta = this.getNumberField(item, ['progressDelta', 'delta']);

    if (before != null && after != null) {
      return `${this.formatCompactNumber(before)}% -> ${this.formatCompactNumber(after)}%`;
    }

    if (delta != null) {
      return `+${this.formatCompactNumber(delta)}%`;
    }

    if (after != null) {
      return `${this.formatCompactNumber(after)}%`;
    }

    return '—';
  }

  public formatLogStatus(item: VipQuestLogItem): string {
    const status = this.getStringField(item, ['status', 'result', 'state', 'outcome']);
    return status || '—';
  }

  private resetViewState(): void {
    this.dashboardRequestVersion += 1;
    this.questLogRequestVersion += 1;
    this.isDashboardLoading = true;
    this.dashboardError = null;
    this.dashboard = null;
    this.questGames = [];
    this.selectedGameSlug = null;
    this.selectedQuestNumber = null;
    this.caseData = [];
    this.questLogItems = [];
    this.questLogMeta = {};
    this.questLogError = null;
    this.isQuestLogLoading = false;
    this.questLogPageIndex = 0;
    this.questLogPageSize = 10;
  }

  private loadDashboard(userId: number): void {
    const requestVersion = this.dashboardRequestVersion;
    this.isDashboardLoading = true;
    this.dashboardError = null;

    this.vipFacade
      .getDashboard(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data: VipDashboardResponse) => {
          if (requestVersion !== this.dashboardRequestVersion) {
            return;
          }

          this.dashboard = data;
          this.caseData = this.mapCaseRows(data.cases ?? []);
          this.questGames = this.mapQuestGames(data.quests ?? []);
          this.selectedGameSlug = this.questGames[0]?.gameSlug || null;
          this.selectedQuestNumber = this.getInitialQuestNumber(this.selectedGame);
          this.isDashboardLoading = false;

          if (this.selectedGame && this.selectedQuestNumber != null) {
            this.loadSelectedQuestLogs();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (requestVersion !== this.dashboardRequestVersion) {
            return;
          }

          this.dashboardError = this.vipFacade.getErrorMessage(error, 'Failed to load VIP data');
          this.isDashboardLoading = false;
        },
      });
  }

  private loadSelectedQuestLogs(): void {
    if (
      this.currentUserId == null ||
      !this.selectedGame ||
      this.selectedQuestNumber == null
    ) {
      return;
    }

    const query: VipQuestLogsQuery = {
      gameSlug: this.selectedGame.gameSlug,
      questNumber: this.selectedQuestNumber,
      page: this.questLogPageIndex,
      pageSize: this.questLogPageSize,
      questDate: this.selectedGame.questDate,
    };

    const requestVersion = ++this.questLogRequestVersion;
    this.isQuestLogLoading = true;
    this.questLogError = null;

    this.vipFacade
      .getQuestLogs(this.currentUserId, query)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: VipQuestLogsResponse) => {
          if (requestVersion !== this.questLogRequestVersion) {
            return;
          }

          this.questLogItems = response?.items || [];
          this.questLogMeta = response?.meta || {};
          this.isQuestLogLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          if (requestVersion !== this.questLogRequestVersion) {
            return;
          }

          this.questLogItems = [];
          this.questLogMeta = {};
          this.questLogError = this.vipFacade.getErrorMessage(error, 'Failed to load quest XP log');
          this.isQuestLogLoading = false;
        },
      });
  }

  private mapQuestGames(quests: VipDashboardQuest[]): VipQuestGameView[] {
    return quests.map((quest: VipDashboardQuest, index: number) => {
      const currentQuestCursor = this.normaliseQuestCursor(quest.currentQuest);
      const currentQuest = this.clampQuestNumber(currentQuestCursor);
      const claimedUpTo = Math.max(0, Math.min(quest.claimedUpTo || 0, 10));
      const currentProgress = Math.max(0, Math.min(quest.currentProgress || 0, 100));
      const gameSlug = quest.gameSlug || quest.gameName || `game-${index + 1}`;
      const gameLabel = quest.gameName || quest.gameSlug || `Игра ${index + 1}`;
      const questStrips = Array.from({ length: 10 }, (_value: unknown, stripIndex: number) =>
        this.buildQuestStrip(stripIndex + 1, currentQuestCursor, claimedUpTo, currentProgress)
      );
      const claimedCount = questStrips.filter((strip: VipQuestStripView) => strip.status === 'claimed').length;
      const readyToClaimCount = questStrips.filter(
        (strip: VipQuestStripView) => strip.status === 'completed-awaiting-claim'
      ).length;

      return {
        gameLabel,
        gameSlug,
        questDate: quest.questDate,
        currentQuest,
        currentQuestCursor,
        currentQuestLabel:
          currentQuestCursor > 10 ? 'все стадии выполнены' : `текущая стадия ${currentQuest}`,
        currentProgress,
        claimedUpTo,
        claimedCount,
        readyToClaimCount,
        completedCount: claimedCount + readyToClaimCount,
        quests: questStrips,
      };
    });
  }

  private buildQuestStrip(
    questNumber: number,
    currentQuestCursor: number,
    claimedUpTo: number,
    currentProgress: number,
  ): VipQuestStripView {
    if (questNumber <= claimedUpTo) {
      return {
        questNumber,
        progressPercent: 100,
        progressLabel: '100%',
        status: 'claimed',
        statusLabel: 'Квест забран',
      };
    }

    if (questNumber < currentQuestCursor) {
      return {
        questNumber,
        progressPercent: 100,
        progressLabel: '100%',
        status: 'completed-awaiting-claim',
        statusLabel: 'Квест не забран',
      };
    }

    if (currentQuestCursor <= 10 && questNumber === currentQuestCursor) {
      const isAwaitingClaim = currentProgress >= 100;

      return {
        questNumber,
        progressPercent: isAwaitingClaim ? 100 : currentProgress,
        progressLabel: `${Math.round(isAwaitingClaim ? 100 : currentProgress)}%`,
        status: isAwaitingClaim ? 'completed-awaiting-claim' : 'in-progress',
        statusLabel: isAwaitingClaim ? 'Квест не забран' : 'В процессе',
      };
    }

    return {
      questNumber,
      progressPercent: 0,
      progressLabel: '0%',
      status: 'locked',
      statusLabel: questNumber === claimedUpTo + 1 ? 'Ожидает прогресс' : 'Закрыт',
    };
  }

  private getInitialQuestNumber(game: VipQuestGameView | null): number | null {
    if (!game) {
      return null;
    }

    if (game.readyToClaimCount > 0) {
      return Math.min(Math.max(game.claimedUpTo + 1, 1), 10);
    }

    if (game.currentQuestCursor >= 1 && game.currentQuestCursor <= 10) {
      return game.currentQuestCursor;
    }

    return Math.min(Math.max(game.claimedUpTo + 1, 1), 10);
  }

  private normaliseQuestCursor(value: number): number {
    if (!Number.isFinite(value)) {
      return 1;
    }

    return Math.max(1, Math.floor(value));
  }

  private mapCaseRows(cases: VipDashboardCase[]): VipCaseRow[] {
    return cases.map((vipCase: VipDashboardCase) => ({
      period: vipCase.period,
      currentStage: vipCase.currentStage,
      valueLabel: `$${(vipCase.valueCents / 100).toFixed(2)}`,
      claimStatus: vipCase.isClaimed ? 'Открыт' : 'Не открыт',
    }));
  }

  private clampQuestNumber(value: number): number {
    if (!value) {
      return 1;
    }

    return Math.max(1, Math.min(value, 10));
  }

  private formatCompactNumber(value: number): string {
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  }

  private getNumberField(item: VipQuestLogItem, keys: string[]): number | null {
    for (const key of keys) {
      const value = item[key];

      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim().length > 0 && !Number.isNaN(Number(value))) {
        return Number(value);
      }
    }

    return null;
  }

  private getStringField(item: VipQuestLogItem, keys: string[]): string | null {
    for (const key of keys) {
      const value = item[key];

      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return null;
  }
}
